'use client';

import { useTranslations } from 'next-intl';
import {
  Type,
  AlignLeft,
  Hash,
  Calendar,
  List,
  CheckSquare,
  Phone,
  AtSign,
  Plus
} from 'lucide-react';
import type { DynamicFieldType } from '../types';

interface FieldTypeSidebarProps {
  onAddField: (type: DynamicFieldType) => void;
}

interface FieldTypeEntry {
  type: DynamicFieldType;
  labelKey: string;
  hintKey: string;
  icon: React.ComponentType<{ className?: string }>;
}

const FIELD_TYPE_GROUPS: {
  titleKey: string;
  types: FieldTypeEntry[];
}[] = [
  {
    titleKey: 'fieldGroupText',
    types: [
      {
        type: 'short_text',
        labelKey: 'fieldShortText',
        hintKey: 'fieldShortTextHint',
        icon: Type
      },
      {
        type: 'long_text',
        labelKey: 'fieldLongText',
        hintKey: 'fieldLongTextHint',
        icon: AlignLeft
      }
    ]
  },
  {
    titleKey: 'fieldGroupData',
    types: [
      {
        type: 'number',
        labelKey: 'fieldNumber',
        hintKey: 'fieldNumberHint',
        icon: Hash
      },
      {
        type: 'date',
        labelKey: 'fieldDate',
        hintKey: 'fieldDateHint',
        icon: Calendar
      }
    ]
  },
  {
    titleKey: 'fieldGroupChoices',
    types: [
      {
        type: 'selection_list',
        labelKey: 'fieldSelectionList',
        hintKey: 'fieldSelectionListHint',
        icon: List
      },
      {
        type: 'yes_no',
        labelKey: 'fieldYesNo',
        hintKey: 'fieldYesNoHint',
        icon: CheckSquare
      }
    ]
  },
  {
    titleKey: 'fieldGroupContact',
    types: [
      {
        type: 'phone',
        labelKey: 'fieldPhone',
        hintKey: 'fieldPhoneHint',
        icon: Phone
      },
      {
        type: 'email',
        labelKey: 'fieldEmail',
        hintKey: 'fieldEmailHint',
        icon: AtSign
      }
    ]
  }
];

export function FieldTypeSidebar({ onAddField }: FieldTypeSidebarProps) {
  const t = useTranslations('dynamicForms');

  return (
    <div className='space-y-4'>
      <h4 className='text-sm font-semibold'>{t('fieldTypes')}</h4>
      {FIELD_TYPE_GROUPS.map((group) => (
        <div key={group.titleKey}>
          <p className='text-muted-foreground mb-1.5 text-xs font-medium tracking-wide uppercase'>
            {t(group.titleKey)}
          </p>
          <div className='space-y-1.5'>
            {group.types.map((ft) => {
              const Icon = ft.icon;
              return (
                <button
                  key={ft.type}
                  type='button'
                  onClick={() => onAddField(ft.type)}
                  className='border-border hover:border-primary/30 hover:bg-muted/50 flex w-full items-center gap-3 rounded-lg border p-2.5 text-start transition-colors'
                >
                  <Plus className='text-primary h-3.5 w-3.5 shrink-0' />
                  <div className='min-w-0 flex-1'>
                    <div className='flex items-center gap-2'>
                      <Icon className='text-muted-foreground h-4 w-4 shrink-0' />
                      <span className='text-sm font-medium'>
                        {t(ft.labelKey)}
                      </span>
                    </div>
                    <p className='text-muted-foreground mt-0.5 text-xs'>
                      {t(ft.hintKey)}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
